import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill-new";
import { useNavigate, useParams } from "react-router-dom";
import blogApi, { fileUrl } from "../apiProvider/blog";

const emptyForm = {
  title: "",
  slug: "",
  excerpt: "",
  contentHtml: "",
  category: "Saree Guides",
  tags: "",
  status: "draft",
  coverImage: {},
  mediaUrls: [],
  metaTitle: "",
  metaDescription: "",
  focusKeyword: "",
  canonicalUrl: "",
  ogTitle: "",
  ogDescription: "",
  ogImage: "",
  isFeatured: false,
};

const slugify = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const AddBlogLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const isEdit = Boolean(id);

  useEffect(() => {
    if (!id) return;
    blogApi.get(id).then((blog) => {
      setForm({
        ...emptyForm,
        ...blog,
        category: blog.category?.name || blog.category || "",
        tags: (blog.tags || []).map((tag) => tag.name || tag).join(", "),
      });
    });
  }, [id]);

  const coverPreview = useMemo(() => fileUrl(form.coverImage), [form.coverImage]);
  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleTitleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      title: value,
      slug: prev.slug ? prev.slug : slugify(value),
      metaTitle: prev.metaTitle ? prev.metaTitle : value,
      ogTitle: prev.ogTitle ? prev.ogTitle : value,
    }));
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    const uploaded = await blogApi.uploadMedia(file, "blog");
    setForm((prev) => ({ ...prev, coverImage: uploaded, ogImage: prev.ogImage || uploaded.url }));
  };

  const handleInlineUpload = async (file) => {
    if (!file) return;
    const uploaded = await blogApi.uploadMedia(file, "blog");
    setMediaUrl(uploaded.url);
    setForm((prev) => ({ ...prev, mediaUrls: [...(prev.mediaUrls || []), uploaded.url] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: slugify(form.slug || form.title),
        tags: form.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
      };
      if (isEdit) await blogApi.update(id, payload);
      else await blogApi.create(payload);
      navigate("/blog");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="row gy-4" onSubmit={handleSubmit}>
      <div className="col-lg-8">
        <div className="card">
          <div className="card-header border-bottom">
            <h6 className="text-xl mb-0">{isEdit ? "Edit Blog Post" : "Add New Blog Post"}</h6>
          </div>
          <div className="card-body p-24 d-flex flex-column gap-20">
            <div>
              <label className="form-label fw-bold text-neutral-900">Post Title</label>
              <input className="form-control" value={form.title} onChange={(e) => handleTitleChange(e.target.value)} required />
            </div>
            <div>
              <label className="form-label fw-bold text-neutral-900">Slug</label>
              <input className="form-control" value={form.slug} onChange={(e) => updateField("slug", slugify(e.target.value))} required />
            </div>
            <div>
              <label className="form-label fw-bold text-neutral-900">Excerpt</label>
              <textarea className="form-control" rows="3" value={form.excerpt} onChange={(e) => updateField("excerpt", e.target.value)} />
            </div>
            <div>
              <label className="form-label fw-bold text-neutral-900">Blog Content</label>
              <ReactQuill
                theme="snow"
                value={form.contentHtml}
                onChange={(value) => updateField("contentHtml", value)}
                placeholder="Write SEO blog content..."
                style={{ minHeight: 260, marginBottom: 48 }}
              />
            </div>
            <div className="row gy-3">
              <div className="col-md-6">
                <label className="form-label fw-bold text-neutral-900">Media Upload URL Tool</label>
                <input type="file" className="form-control" accept="image/*" onChange={(e) => handleInlineUpload(e.target.files?.[0])} />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold text-neutral-900">Latest Uploaded URL</label>
                <input className="form-control" value={mediaUrl} readOnly placeholder="Upload media to get URL" />
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-24">
          <div className="card-header border-bottom"><h6 className="text-xl mb-0">SEO Settings</h6></div>
          <div className="card-body p-24 row gy-3">
            <div className="col-md-6"><label className="form-label">SEO Title</label><input className="form-control" value={form.metaTitle} onChange={(e) => updateField("metaTitle", e.target.value)} /></div>
            <div className="col-md-6"><label className="form-label">Focus Keyword</label><input className="form-control" value={form.focusKeyword} onChange={(e) => updateField("focusKeyword", e.target.value)} /></div>
            <div className="col-12"><label className="form-label">Meta Description</label><textarea className="form-control" rows="3" value={form.metaDescription} onChange={(e) => updateField("metaDescription", e.target.value)} /></div>
            <div className="col-12"><label className="form-label">Canonical URL</label><input className="form-control" value={form.canonicalUrl} onChange={(e) => updateField("canonicalUrl", e.target.value)} placeholder="https://coloursboutique.com/blog/post-slug" /></div>
            <div className="col-md-6"><label className="form-label">OG Title</label><input className="form-control" value={form.ogTitle} onChange={(e) => updateField("ogTitle", e.target.value)} /></div>
            <div className="col-md-6"><label className="form-label">OG Image URL</label><input className="form-control" value={form.ogImage} onChange={(e) => updateField("ogImage", e.target.value)} /></div>
            <div className="col-12"><label className="form-label">OG Description</label><textarea className="form-control" rows="2" value={form.ogDescription} onChange={(e) => updateField("ogDescription", e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div className="col-lg-4">
        <div className="card">
          <div className="card-header border-bottom"><h6 className="text-xl mb-0">Publish Settings</h6></div>
          <div className="card-body p-24 d-flex flex-column gap-20">
            <div><label className="form-label fw-bold text-neutral-900">Category</label><input className="form-control" value={form.category} onChange={(e) => updateField("category", e.target.value)} /></div>
            <div><label className="form-label fw-bold text-neutral-900">Tags</label><input className="form-control" value={form.tags} onChange={(e) => updateField("tags", e.target.value)} placeholder="silk saree, singapore, kanchipuram" /></div>
            <div>
              <label className="form-label fw-bold text-neutral-900">Status</label>
              <select className="form-control" value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div>
              <label className="form-label fw-bold text-neutral-900">Cover Image</label>
              <input type="file" className="form-control" accept="image/*" onChange={(e) => handleCoverUpload(e.target.files?.[0])} />
              {coverPreview && <img src={coverPreview} alt="" className="w-100 h-160-px object-fit-cover radius-8 mt-12" />}
            </div>
            <button type="submit" className="btn btn-primary-600 radius-8" disabled={saving}>{saving ? "Saving..." : "Save Blog"}</button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AddBlogLayer;
